// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect, useState } from "react";
import Alert from "@cloudscape-design/components/alert";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Checkbox from "@cloudscape-design/components/checkbox";
import type { ILocalAgentClient } from "../../services/localAgentClient";
import type { WizardStepProps } from "../EnrollmentWizard";

export interface ConnectYubiKeyStepProps extends WizardStepProps {
  localAgentClient: ILocalAgentClient;
}

export default function ConnectYubiKeyStep({
  state,
  setState,
  onComplete,
  onError,
  localAgentClient,
}: ConnectYubiKeyStepProps): React.ReactElement {
  const [agentReachable, setAgentReachable] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [slotOccupied, setSlotOccupied] = useState(false);
  const [overwriteConfirmed, setOverwriteConfirmed] = useState(false);
  const [serial, setSerial] = useState<string | null>(null);
  const [slotSubject, setSlotSubject] = useState<string | undefined>();
  const [slotExpiry, setSlotExpiry] = useState<string | undefined>();

  const detect = async () => {
    setLoading(true);
    setAgentReachable(null);
    try {
      await localAgentClient.checkHealth();
      setAgentReachable(true);
    } catch {
      setAgentReachable(false);
      setLoading(false);
      onError("Unable to reach the Local Agent. Please start it and retry.");
      return;
    }

    try {
      const info = await localAgentClient.getYubiKeyInfo();
      setSerial(info.serial_number);
      setSlotOccupied(info.slot_9a.occupied);
      setSlotSubject(info.slot_9a.certificate_subject);
      setSlotExpiry(info.slot_9a.certificate_not_after);

      setState((prev) => ({
        ...prev,
        yubiKeySerial: info.serial_number,
      }));

      if (!info.slot_9a.occupied) {
        onComplete();
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to read YubiKey info.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (agentReachable === false) {
    return (
      <SpaceBetween size="m">
        <Alert
          type="warning"
          header="Local Agent not reachable"
          action={
            <Button onClick={() => void detect()} loading={loading}>
              Retry
            </Button>
          }
        >
          The YubiKey Local Agent must be running on your machine. Start it with{" "}
          <code>yubikey-agent</code> and click Retry.
        </Alert>
      </SpaceBetween>
    );
  }

  if (loading) {
    return (
      <Container>
        <StatusIndicator type="loading">Detecting YubiKey…</StatusIndicator>
      </Container>
    );
  }

  if (serial && slotOccupied && !overwriteConfirmed) {
    return (
      <SpaceBetween size="m">
        <Container header={<Header variant="h3">YubiKey Detected</Header>}>
          <SpaceBetween size="s">
            <StatusIndicator type="success">
              Serial: {serial}
            </StatusIndicator>
            <Alert type="warning" header="Slot 9a is occupied">
              {slotSubject && <p>Current certificate subject: {slotSubject}</p>}
              {slotExpiry && <p>Expires: {slotExpiry}</p>}
              Proceeding will overwrite the existing key and certificate in slot 9a.
            </Alert>
            <Checkbox
              checked={overwriteConfirmed}
              onChange={({ detail }) => setOverwriteConfirmed(detail.checked)}
            >
              I confirm I want to overwrite the existing key in slot 9a
            </Checkbox>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    );
  }

  if (serial && slotOccupied && overwriteConfirmed) {
    return (
      <SpaceBetween size="m">
        <Container header={<Header variant="h3">YubiKey Detected</Header>}>
          <SpaceBetween size="s">
            <StatusIndicator type="success">
              Serial: {serial}
            </StatusIndicator>
            <StatusIndicator type="info">
              Slot 9a will be overwritten
            </StatusIndicator>
            <Button variant="primary" onClick={onComplete}>
              Continue
            </Button>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    );
  }

  if (serial) {
    return (
      <Container header={<Header variant="h3">YubiKey Detected</Header>}>
        <SpaceBetween size="s">
          <StatusIndicator type="success">
            Serial: {serial}
          </StatusIndicator>
          <StatusIndicator type="success">
            Slot 9a is available
          </StatusIndicator>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <StatusIndicator type="pending">Waiting for YubiKey detection…</StatusIndicator>
    </Container>
  );
}
